import { db, credenzialiUtente } from "./firebase";
import { collection, doc, setDoc, deleteDoc, updateDoc, deleteField, onSnapshot, Timestamp, query, where, writeBatch, arrayUnion, getDocs } from 'firebase/firestore';
import { impostazioniGenerali, generaIdCliente, generaStringaCliente, generaIdContatto, ricercaCompleta, numGiorniDocumentiVecchi, ultimaVersione } from "./utilita";
import { isRef, ref, reactive, toRaw, markRaw } from 'vue';
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";

export const dbClienti = ref(null);
export const dbContatti = ref(null);
export const dbListini = ref(null);
const infoDB = new WeakMap();
export let unsubClienti = null;

/**
 * Carica i dati freddi dallo Storage e li unisce ai database locali
 */
export async function caricaDatiFreddiDaStorage() {
    if (!dbClienti.value) dbClienti.value = {};
    if (!dbContatti.value) dbContatti.value = {};
    if (!dbListini.value) dbListini.value = {};

    const storage = getStorage();
    const files = [
        { nome: 'clienti', db: dbClienti },
        { nome: 'contatti', db: dbContatti }
    ];
    
    // Creiamo una chiave di cache che cambia ogni giorno
    const oggi = new Date().toISOString().split('T')[0];
    const cacheNome = `lmc-dati-freddi-${oggi}`;

    for (const file of files) {
        try {
            const cache = await caches.open(cacheNome);
            let response = await cache.match(file.nome);

            if (!response) {
                // Cache miss: scarichiamo da Storage
                const url = await getDownloadURL(storageRef(storage, `ricerca/${file.nome}.json`));
                response = await fetch(url);
                
                // Salviamo un clone in cache prima di consumarlo
                cache.put(file.nome, response.clone());
                console.log(`[STORAGE] Scaricato e cachato ${file.nome}.`);
            } else {
                console.log(`[CACHE] ${file.nome} recuperato da cache locale.`);
            }

            const datiFreddi = await response.json();
            
            // ... [Mantieni il resto del tuo codice per il merge markRaw]
            for (const id in datiFreddi) {
                if (!file.db.value[id]) {
                    file.db.value[id] = markRaw(datiFreddi[id]);
                }
            }
        } catch (e) {
            console.error(`Errore nel caricamento del file freddo ${file.nome}:`, e);
        }
    }
}

export async function caricaVersioneDaFirestore() {
    // Carica le informazioni sull'ultima versione da firestore
    if (credenzialiUtente.value) {
        const q = doc(db, 'utilita', 'versione');
        onSnapshot(q, (doc) => {
            ultimaVersione.value = doc.data();
            // verificaAggiornamentoVersione();
        });
    }
}

export async function caricaImpostazioniDaFirestore() {
    if (credenzialiUtente.value) {
        const q = doc(db, 'utilita', 'impostazioni');
        onSnapshot(q, (doc) => {
            impostazioniGenerali.value = doc.data();
        });
    }
}

/**
 * Scarica solo i contatti "caldi" (modificati di recente) dalla nuova collection
 */
export function caricaDbContattiDaFirestore() {
    return new Promise((resolve, reject) => {

        // --- INIZIALIZZAZIONE GLOBALE DI SICUREZZA ---
        if (!dbClienti.value) dbClienti.value = {};
        if (!dbContatti.value) dbContatti.value = {};
        if (!dbListini.value) dbListini.value = {};
        // ---------------------------------------------

        // Calcola la data limite
        const dataLimite = new Date();
        dataLimite.setDate(dataLimite.getDate() - numGiorniDocumentiVecchi.value);
        
        // Creiamo la query limitata
        const q = query(
            collection(db, 'contatti'),
            where('modified', '>=', Timestamp.fromDate(dataLimite))
        );

        onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                const id = change.doc.id;
                const data = change.doc.data();

                if (change.type === "added" || change.type === "modified") {
                    if (!dbContatti.value) {
                        dbContatti.value = {};
                    }
                    // Sovrascrive il dato e lo rende reattivo
                    dbContatti.value[id] = data;

                    // FIX: Inizializziamo infoDB per i contatti "Caldi" appena arrivano da Firestore
                    // Assicurandoci che l'id sia un Number (poiché Firestore usa stringhe per i doc.id)
                    aggiungiInfoDB(dbContatti.value[id], {
                        id: Number(id),
                        leads: {},   // Inizializziamo i contenitori per evitare successivi crash
                        lavori: {}
                    });
                }
                
                if (change.type === "removed") {
                    delete dbContatti.value[id];
                }
            });
            resolve(true);
        }, (error) => {
            console.error("Errore nel caricamento dei contatti caldi:", error);
            reject(error);
        });
    });
}

export function popolaInfoContattiDaClienti() {
    // Aggiunge l'elenco dei lavori in tutti i contatti
    // Deve essere lanciata dopo aver caricato dbClienti
    for (const [idCl, cl] of Object.entries(dbClienti.value)) {
        if (cl.leads) {
            for (const [idLd, ld] of Object.entries(cl.leads)) {
                if (ld.contatti) {
                    for (const [idCt, ct] of Object.entries(ld.contatti)) {
                        // FIX: Verifichiamo che il contatto esista in RAM prima di incrociarlo
                        if (dbContatti.value[idCt]) {
                            aggiungiInfoDB(dbContatti.value[idCt], {
                                leads: {
                                    [idLd]: {
                                        cliente: Number(idCl),
                                        ruolo: ct.ruolo
                                    }
                                }
                            });
                        }
                    }
                }
            }
        }
        if (cl.lavori) {
            for (const [idLv, lv] of Object.entries(cl.lavori)) {
                if (lv.contatti) {
                    for (const [idCt, ct] of Object.entries(lv.contatti)) {
                        // FIX: Verifichiamo che il contatto esista in RAM prima di incrociarlo
                        if (dbContatti.value[idCt]) {
                            aggiungiInfoDB(dbContatti.value[idCt], {
                                lavori: {
                                    [idLv]: {
                                        cliente: Number(idCl),
                                        ruolo: ct.ruolo
                                    }
                                }
                            });
                        }
                    }
                }
            }
        }
    }
}

export async function caricaDbListiniDaFirestore() {
    // Carica i listini da firestore in dbListini
    if (credenzialiUtente.value) {
        const q = collection(db, 'listini');

        dbListini.value = {};
        onSnapshot(q, (querySnapshot) => {
            querySnapshot.forEach((listino) => {
                dbListini.value[listino.id] = listino.data();
                // aggiungiInfoListino(listino.id);
                aggiungiInfoDB(dbListini.value[listino.id], {
                    id: parseInt(listino.id)
                });
                // aggiungiInfoArticoliListino(listino.id);
                for (const art in dbListini.value[listino.id].articoli) {
                    aggiungiInfoDB(dbListini.value[listino.id].articoli[art], {
                        id: art
                    });
                }
            });
        });
    }
}

export async function salvaArticoloListinoInFirestore(art, lst) {
    // Salva un articolo in un listino
    // art contiene l'articolo da salvare con la proprietà info.id
    // lst contiene l'id del listino
    // op contiene l'operazione da effettuare: 'aggiungi' o 'modifica'
    // Restituisce 1 se ok, 2 se sto tentando di sovrascrivere un articolo esistente,
    // 0 in caso di fallimento per altra ragione

    let tempListino = {};
    tempListino.articoli = {};
    tempListino.articoli[leggiInfoDBRaw(art).id] = {};
    for (const property in art) {
        tempListino.articoli[leggiInfoDBRaw(art).id][property] = art[property];
    }
    // Elimino le proprietà aggiunte (info)
    // delete tempListino.articoli[leggiInfoDBRaw(art).id].info;
    try {
        // Operazioni in batch
        const batch = writeBatch(db);
        // Verifica se l'articolo esiste già e lo elimina
        if (dbListini.value[lst].articoli[leggiInfoDBRaw(art).id]) {
            batch.update(doc(db, 'listini', String(lst)),{
                ['articoli.' + leggiInfoDBRaw(art).id]: deleteField()
            });
        }
        batch.set(doc(db, 'listini', String(lst)),
            tempListino, { merge: true }
        );
        await batch.commit();
        return 1;
    }
    catch(e) {
        console.error('Errore durante il salvataggio in batch di un articolo: ' + e);
        return 0;
    }
}

export async function eliminaArticoloListinoInFirestore(art, lst) {
    // Elimina un articolo da un listino
    // art contiene l'articolo da eliminare con la proprietà info.id
    // lst contiene l'id del listino
    // Restituisce 1 se ok, 0 in caso di fallimento
    try {
        await updateDoc(doc(db, 'listini', String(lst)), {
            ['articoli.' + art]: deleteField()
        });
        // delete dbListini.value[lst].articoli[art.info.id];
        return 1;
    } catch(e) {
        console.error('Errore durante l\'eliminazione di un articolo dal listino: ' + e);
        return 0;
    }
}

export async function salvaContattoInFirestore(contatto) {
    // Aggiunge il contatto se non ha un id, altrimenti effettua un merge
    // Modifica il contatto come caricato su FireStore
    // Restituisce 1 se ok, 0 in caso di fallimento

    if (!leggiInfoDB(contatto)) {
        // Il contatto non ha un id (è un nuovo contatto)
        aggiungiInfoDB(contatto, {
            id: generaIdContatto()
        });
        contatto.created = Timestamp.now();
    }
    contatto.modified = Timestamp.now();

    // Recuperiamo l'ID per usarlo come nome del documento in Firestore
    const idContatto = leggiInfoDBRaw(contatto).id;

    // Creiamo il payload copiando le proprietà (esattamente come facevi prima,
    // ma senza nidificarle sotto l'ID)
    let payload = {};
    for (const property in contatto) {
        payload[property] = contatto[property];
    }

    try {
        // NOVITÀ: Salviamo direttamente nella collection 'contatti' usando l'idContatto come documento
        await setDoc(doc(db, 'contatti', String(idContatto)),
            payload, { merge: true }
        );
        return 1;
    } catch(e) {
        console.error('Errore durante il salvataggio di un contatto: ' + e);
        return 0;
    }
}

export async function ricaricaDbClientiDaFirestore() {
    unsubClienti();
    caricaDbClientiDaFirestore();
}

export function caricaDbClientiDaFirestore() {
    return new Promise((resolve, reject) => {
        // Se non ci sono le credenziali, risolviamo subito per non bloccare l'app
        if (!credenzialiUtente.value) {
            resolve(false);
            return;
        }
        // --- INIZIALIZZAZIONE GLOBALE DI SICUREZZA ---
        if (!dbClienti.value) dbClienti.value = {};
        if (!dbContatti.value) dbContatti.value = {};
        if (!dbListini.value) dbListini.value = {};
        // ---------------------------------------------
        const collezione = collection(db, 'clienti');
        
        // Calcolo della data di riferimento (es. ultimi 35 giorni)
        const dataJS = new Date();
        dataJS.setDate(dataJS.getDate() - numGiorniDocumentiVecchi.value);
        dataJS.setHours(0, 0, 0, 0); // Azzera l'orario
        const dataRif = Timestamp.fromDate(dataJS);

        let ricerca;
        // ATTENZIONE: Con la nuova architettura, ricercaCompleta a 'true' scaricherà tutto 
        // Firestore vanificando il file Storage. Usalo solo se strettamente necessario.
        if (ricercaCompleta.value == false) {
            ricerca = query(collezione, where('modified', '>', dataRif));
        } else {
            ricerca = collezione; 
        }

        // ELIMINATO: dbClienti.value = {}; -> DISTRUGGEREBBE I DATI FREDDI DELLO STORAGE!

        unsubClienti = onSnapshot(ricerca, (querySnapshot) => {
            
            // Determina la sorgente dei dati (utile per il debug)
            const sorgente = querySnapshot.metadata.fromCache ? "CACHE LOCALE" : "SERVER";
            console.log(`[DEBUG Clienti] Sorgente: ${sorgente} | Snapshot size: ${querySnapshot.size}`);

            // Usiamo docChanges() per processare SOLO le differenze ed effettuare il merge
            querySnapshot.docChanges().forEach((change) => {
                const id = change.doc.id;
                const data = change.doc.data();

                if (change.type === "added" || change.type === "modified") {
                    if (!dbClienti.value) {
                        dbClienti.value = {};
                    }
                    // Sovrascrive il dato e lo rende reattivo
                    dbClienti.value[id] = data;
                    // Popola la WeakMap (infoDB) solo per questo documento caldo
                    popolaInfoCliente(id, dbClienti.value[id]);
                }

                if (change.type === "removed") {
                    // Gestione "Dead Links": se un cliente viene eliminato dal server, lo togliamo dall'UI
                    delete dbClienti.value[id];
                }
            });

            // NOVITÀ: Al termine della ricezione di un blocco di modifiche,
            // ricalcoliamo i legami tra clienti e contatti per l'interfaccia.
            if (typeof popolaInfoContattiDaClienti === 'function') {
                popolaInfoContattiDaClienti();
            }

            // Opzionale: log per capire se ci sono modifiche locali non ancora inviate
            if (querySnapshot.metadata.hasPendingWrites) {
                console.log("[DEBUG Clienti] Ci sono modifiche locali in attesa di invio al server...");
            }

            // Risolve la Promise sbloccando Promise.all in firebase.js
            // (Viene chiamato al primissimo caricamento)
            resolve(true);

        }, (error) => {
            console.error('Errore durante il caricamento Clienti da Firestore:', error);
            reject(error);
        });
    });
}

function popolaInfoCliente(idCl, cl) {
    // Aggiunge tutti i campi info nella WeakMap
    aggiungiInfoDB(cl, {id: Number(idCl)});
    if (cl.leads) {
        for (const [idLd, ld] of Object.entries(cl.leads)) {
            aggiungiInfoDB(ld, {
                id: Number(idLd),
                idCliente: Number(idCl),
                stringaCliente: generaStringaCliente(Number(idCl))
            });
            let tempTotPreventiviAttivi = 0;
            let tempTotPreventivi = 0;
            if (ld.preventivi) {
                for (const [idPr, pr] of Object.entries(ld.preventivi)) {
                    aggiungiInfoDB(pr, {
                        id: Number(idPr),
                        idCliente: Number(idCl),
                        idLead: Number(idLd),
                        stringaCliente: generaStringaCliente(Number(idCl)),
                        stringaLead: generaStringaCliente(Number(idCl), Number(idLd), 'lead'),
                        stringaLeadCorta: generaStringaCliente(Number(idCl), Number(idLd), 'leadCorta')
                    });
                    if (pr.attivo) {
                        tempTotPreventiviAttivi += pr.importo? pr.importo: 0;
                    }
                    tempTotPreventivi += pr.importo? pr.importo: 0;
                    if (pr.voci) {
                        for (const [idVc, vc] of Object.entries(pr.voci)) {
                            aggiungiInfoDB(vc, {
                                id: Number(idVc)
                            });
                        }
                    }
                }
            }
            if (tempTotPreventiviAttivi>0){
                aggiungiInfoDB(ld, {
                    totPreventiviAttivi: tempTotPreventiviAttivi,
                    totPreventivi: tempTotPreventivi
                });
            } else {
                // Vecchio sistema di registrazione importi
                aggiungiInfoDB(ld, {
                    totPreventiviAttivi: ld.primaOffertaImporto,
                    totPreventivi: tempTotPreventivi
                });
            }
            // Carica l'elenco lead nei contatti
            if (ld.contatti) {
                for (const [idCt, ct] of Object.entries(ld.contatti)) {
                    const infoContatto = leggiInfoDBRaw(dbContatti.value[idCt]);
                    // Only try to link if the contact info has been initialized
                    if (infoContatto) {
                        if (!infoContatto.leads) infoContatto.leads = {}; // Ensure the leads object exists
                        infoContatto.leads[idLd] = {
                            id: Number(idCt),
                            cliente: Number(idCl),
                            ruolo: ct.ruolo
                        };
                    }
                }
            }
        }
    }
    if (cl.lavori) {
        for (const [idLv, lv] of Object.entries(cl.lavori)) {
            aggiungiInfoDB(lv, {
                id: Number(idLv),
                idCliente: Number(idCl),
                stringaCliente: generaStringaCliente(Number(idCl)),
                stringaLavoro: generaStringaCliente(Number(idCl), Number(idLv), 'lavoro'),
                stringaLavoroCorta: generaStringaCliente(Number(idCl), Number(idLv), 'lavoroCorta')
            });
            if (lv.fasi) {
                for (const [idFs, fs] of Object.entries(lv.fasi)) {
                    aggiungiInfoDB(fs, {
                        id: Number(idFs),
                        idCliente: Number(idCl),
                        idLavoro: Number(idLv),
                        stringaCliente: generaStringaCliente(Number(idCl)),
                        stringaLavoro: generaStringaCliente(Number(idCl), Number(idLv), 'lavoro')
                    });
                }
            }
            let tempTotCPreventivi = 0;
            let tempTotCConsuntivi = 0;
            let tempTotCFatture = 0;
            let tempTotCSbilancio = 0;
            if (lv.contabili) {
                for (const [idCc, cc] of Object.entries(lv.contabili)) {
                    aggiungiInfoDB(cc, {
                        id: Number(idCc),
                        idCliente: Number(idCl),
                        idLavoro: Number(idLv),
                        stringaCliente: generaStringaCliente(Number(idCl)),
                        stringaLavoro: generaStringaCliente(Number(idCl), Number(idLv), 'lavoro')
                    });
                    tempTotCPreventivi += cc.preventivo? cc.preventivo: 0;
                    tempTotCConsuntivi  += cc.consuntivo? cc.consuntivo: 0;
                    tempTotCFatture  += cc.fattura? cc.fattura: 0;
                    tempTotCSbilancio += cc.consuntivo? cc.consuntivo: 0;
                    tempTotCSbilancio -= cc.fattura? cc.fattura: 0;
                }
            }

            aggiungiInfoDB(lv, {
                totPreventivi: tempTotCPreventivi,
                totConsuntivi: tempTotCConsuntivi,
                totFatture: tempTotCFatture,
                totSbilancio: tempTotCSbilancio
            });

            if (lv.dico) {
                for (const [idDc, dc] of Object.entries(lv.dico)) {
                    aggiungiInfoDB(dc, {
                        id: Number(idDc),
                        idCliente: Number(idCl),
                        idLavoro: Number(idLv),
                        stringaCliente: generaStringaCliente(Number(idCl)),
                        stringaLavoro: generaStringaCliente(Number(idCl), Number(idLv), 'lavoro')
                    });
                }
            }
            if (lv.costi) {
                for (const [idCc, cc] of Object.entries(lv.costi)) {
                    aggiungiInfoDB(cc, {
                        id: Number(idCc),
                        idCliente: Number(idCl),
                        idLavoro: Number(idLv),
                        stringaCliente: generaStringaCliente(Number(idCl)),
                        stringaLavoro: generaStringaCliente(Number(idCl), Number(idLv), 'lavoro'),
                        stringaLavoroCorta: generaStringaCliente(Number(idCl), Number(idLv), 'lavoroCorta')
                    });
                }
            }

            // Carica l'elenco lavori nei contatti (VERSIONE PROTETTA)
            if (lv.contatti) {
                for (const [idCt, ct] of Object.entries(lv.contatti)) {
                    const infoContatto = leggiInfoDBRaw(dbContatti.value[idCt]);
                    
                    // Solo se il contatto è già stato caricato in RAM
                    if (infoContatto) {
                        if (!infoContatto.lavori) infoContatto.lavori = {};
                        infoContatto.lavori[idLv] = {
                            id: Number(idCt),
                            cliente: Number(idCl),
                            ruolo: ct.ruolo
                        };
                    }
                }
            }
        }
    }


}

export async function salvaClienteInFirestore(cliente, dataModifica) {
    // Aggiunge il cliente se non ha un id, altrimenti effetua un merge
    // Modifica il cliente caricato su FireStore
    // Se non voglio aggiornare la data di modifica non passo dataModifica
    // (ad esempio se sto caricando il cliente da un backup)
    // Restituisce 1 se ok, 0 in caso di fallimento

    try {
        // await setDoc(doc(db, 'clienti', String(cliente.info.id)),
        await setDoc(doc(db, 'clienti', String(leggiInfoDB(cliente).id)),
            creaClientePerSalvataggio(cliente, dataModifica), { merge: true }
        );
        return 1;
    } catch(e) {
        console.error('Errore durante il salvataggio di un cliente: ' + e);
        return 0;
    }
}

function creaClientePerSalvataggio(cliente, dataModifica){
    // 1. Regolazione del Codice (come facevi prima)
    if (cliente.codice !== undefined) {
        const tempCodice = parseInt(cliente.codice);
        cliente.codice = tempCodice ? tempCodice : 0;
    }

    // 2. Gestione degli ID e delle Date (Essenziale per la promozione)
    const info = leggiInfoDB(cliente);
    if (!info || !info.id) {
        // È un cliente nuovo di zecca
        if (!dataModifica) dataModifica = Timestamp.now();
        aggiungiInfoDB(cliente, { id: generaIdCliente() });
        cliente.created = dataModifica;
        cliente.modified = dataModifica;
    } else {
        // È un cliente esistente (Caldo o Freddo)
        // FORZIAMO l'aggiornamento a "adesso" per farlo intercettare dal Listener Caldo
        cliente.modified = dataModifica || Timestamp.now();

        // Ripristino vecchie date (da JSON Storage freddo potrebbero arrivare come oggetti piatti)
        if (cliente.created && cliente.created.seconds) {
            cliente.created = Timestamp.fromDate(new Date(cliente.created.seconds * 1000));
        }
    }

    // 3. LA MAGIA: Restituiamo una copia perfetta ma sterilizzata da '_info'
    return pulisciPayloadDaInfo(cliente);
}

export async function salvaLeadInFirestore(lead) {
    // Prepara il salvataggio del cliente
    // Restituisce 1 se ok, 0 in caso di fallimento

    // Creo un cliente temporaneo per il salvataggio
    let tempCliente = {}
    aggiungiInfoDB(tempCliente, {
        id: leggiInfoDB(lead).idCliente
    });
    tempCliente.leads = {};
    tempCliente.leads[leggiInfoDB(lead).id] = {};
    tempCliente.leads[leggiInfoDB(lead).id] = lead;
    try {
        await setDoc(doc(db, 'clienti', String(leggiInfoDB(tempCliente).id)),
            creaClientePerSalvataggio(tempCliente, Timestamp.now()), { merge: true }
        );
        return 1;
    } catch(e) {
        console.error('Errore durante il salvataggio di un cliente: ' + e);
        return 0;
    }
}

export async function salvaLavoroInFirestore(lavoro) {
    // Prepara il salvataggio del cliente
    // Restituisce 1 se ok, 0 in caso di fallimento

    let tempCliente = {}
    aggiungiInfoDB(tempCliente, {
        id: leggiInfoDB(lavoro).idCliente
    });
    tempCliente.lavori = {};
    tempCliente.lavori[leggiInfoDB(lavoro).id] = {};
    tempCliente.lavori[leggiInfoDB(lavoro).id] = lavoro;
    // return await salvaClienteInFirestore(tempCliente, Timestamp.now());
    try {
        await setDoc(doc(db, 'clienti', String(leggiInfoDB(tempCliente).id)),
            creaClientePerSalvataggio(tempCliente, Timestamp.now()), { merge: true }
        );
        return 1;
    } catch(e) {
        console.error('Errore durante il salvataggio di un cliente: ' + e);
        return 0;
    }
}

export async function salvaFaseInFirestore(fase) {
    // Prepara il salvataggio del cliente
    // Restituisce 1 se ok, 0 in caso di fallimento

    let tempCliente = {}
    aggiungiInfoDB(tempCliente, {
        id: leggiInfoDB(fase).idCliente
    });
    tempCliente.lavori = {};
    tempCliente.lavori[leggiInfoDB(fase).idLavoro] = {};
    tempCliente.lavori[leggiInfoDB(fase).idLavoro].fasi = {};
    tempCliente.lavori[leggiInfoDB(fase).idLavoro].fasi[leggiInfoDB(fase).id] = {};
    tempCliente.lavori[leggiInfoDB(fase).idLavoro].fasi[leggiInfoDB(fase).id] = fase;
    // return await salvaClienteInFirestore(tempCliente, Timestamp.now());
    try {
        await setDoc(doc(db, 'clienti', String(leggiInfoDB(tempCliente).id)),
            creaClientePerSalvataggio(tempCliente, Timestamp.now()), { merge: true }
        );
        return 1;
    } catch(e) {
        console.error('Errore durante il salvataggio di un cliente: ' + e);
        return 0;
    }
}

export async function salvaCostoInFirestore(costo, costoDaEliminare = null) {
    // Prepara il salvataggio del cliente
    // Elimina costoDaEliminare {idCliente, idLavoro, idCosto} in caso di spostamento
    // Restituisce 1 se ok, 0 in caso di fallimento

    let tempCliente = {}
    aggiungiInfoDB(tempCliente, {
        id: leggiInfoDB(costo).idCliente
    });
    tempCliente.lavori = {};
    tempCliente.lavori[leggiInfoDB(costo).idLavoro] = {};
    tempCliente.lavori[leggiInfoDB(costo).idLavoro].costi = {};
    tempCliente.lavori[leggiInfoDB(costo).idLavoro].costi[leggiInfoDB(costo).id] = {};
    tempCliente.lavori[leggiInfoDB(costo).idLavoro].costi[leggiInfoDB(costo).id] = costo;
    try {
        const batch = writeBatch(db);
        batch.set(doc(db, 'clienti', String(leggiInfoDB(tempCliente).id)),
            creaClientePerSalvataggio(tempCliente, Timestamp.now()), { merge: true }
        );
        if (costoDaEliminare) {
            const strField = 'lavori.' + costoDaEliminare.idLavoro + '.costi.' + costoDaEliminare.idCosto;
            batch.update(doc(db, 'clienti', String(costoDaEliminare.idCliente)),{
                [strField]: deleteField()
            });
        }
        await batch.commit();

        // await setDoc(doc(db, 'clienti', String(leggiInfoDB(tempCliente).id)),
        //     creaClientePerSalvataggio(tempCliente, Timestamp.now()), { merge: true }
        // );
        return 1;
    } catch(e) {
        console.error('Errore durante il salvataggio di un cliente: ' + e);
        return 0;
    }
}

export async function salvaContabileInFirestore(contabile) {
    // Prepara il salvataggio del cliente
    // Restituisce 1 se ok, 0 in caso di fallimento

    let tempCliente = {}
    aggiungiInfoDB(tempCliente, {
        id: leggiInfoDB(contabile).idCliente
    });
    tempCliente.lavori = {};
    tempCliente.lavori[leggiInfoDB(contabile).idLavoro] = {};
    tempCliente.lavori[leggiInfoDB(contabile).idLavoro].contabili = {};
    tempCliente.lavori[leggiInfoDB(contabile).idLavoro].contabili[leggiInfoDB(contabile).id] = {};
    tempCliente.lavori[leggiInfoDB(contabile).idLavoro].contabili[leggiInfoDB(contabile).id] = contabile;
    // return await salvaClienteInFirestore(tempCliente, Timestamp.now());
    try {
        await setDoc(doc(db, 'clienti', String(leggiInfoDB(tempCliente).id)),
            creaClientePerSalvataggio(tempCliente, Timestamp.now()), { merge: true }
        );
        return 1;
    } catch(e) {
        console.error('Errore durante il salvataggio di un cliente: ' + e);
        return 0;
    }
}

export async function salvaDicoInFirestore(dico) {
    // Prepara il salvataggio del cliente
    // Restituisce 1 se ok, 0 in caso di fallimento

    let tempCliente = {}
    aggiungiInfoDB(tempCliente, {
        id: leggiInfoDB(dico).idCliente
    });
    // tempCliente.info = {};
    // tempCliente.info.id = dico.info.idCliente;
    tempCliente.lavori = {};
    tempCliente.lavori[leggiInfoDB(dico).idLavoro] = {};
    tempCliente.lavori[leggiInfoDB(dico).idLavoro].dico = {};
    tempCliente.lavori[leggiInfoDB(dico).idLavoro].dico[leggiInfoDB(dico).id] = {};
    tempCliente.lavori[leggiInfoDB(dico).idLavoro].dico[leggiInfoDB(dico).id] = dico;
    // return await salvaClienteInFirestore(tempCliente, Timestamp.now());
    try {
        await setDoc(doc(db, 'clienti', String(leggiInfoDB(tempCliente).id)),
            creaClientePerSalvataggio(tempCliente, Timestamp.now()), { merge: true }
        );
        return 1;
    } catch(e) {
        console.error('Errore durante il salvataggio di un cliente: ' + e);
        return 0;
    }
}

export async function salvaPreventivoInFirestore(preventivo) {
    // Prepara il salvataggio del cliente
    // Restituisce 1 se ok, 0 in caso di fallimento
    // Se sono presenti voci di preventivo le elimina tutte prima di riscriverle

    let tempCliente = {}
    aggiungiInfoDB(tempCliente, {
        id: leggiInfoDB(preventivo).idCliente
    });
    // tempCliente.info = {};
    // tempCliente.info.id = preventivo.info.idCliente;
    tempCliente.leads = {};
    tempCliente.leads[leggiInfoDB(preventivo).idLead] = {};
    tempCliente.leads[leggiInfoDB(preventivo).idLead].preventivi = {};
    tempCliente.leads[leggiInfoDB(preventivo).idLead].preventivi[leggiInfoDB(preventivo).id] = {};
    tempCliente.leads[leggiInfoDB(preventivo).idLead].preventivi[leggiInfoDB(preventivo).id] = preventivo;

    // Se sono presenti voci di preventivo le elimina tutte prima di riscriverle
    if (tempCliente.leads[leggiInfoDB(preventivo).idLead].preventivi[leggiInfoDB(preventivo).id].voci) {
        try {
            // Operazioni in batch
            const batch = writeBatch(db);
            // Elimina tutte le voci di preventivo
            batch.update(doc(db, 'clienti', String(leggiInfoDB(tempCliente).id)),{
                ['leads.' + leggiInfoDB(preventivo).idLead + '.preventivi.' + leggiInfoDB(preventivo).id + '.voci']: deleteField()
            });
            batch.set(doc(db, 'clienti', String(leggiInfoDB(tempCliente).id)),
                creaClientePerSalvataggio(tempCliente, Timestamp.now()), { merge: true }
            );
            await batch.commit();
            return 1;
        }
        catch(e) {
            console.error('Errore durante il salvataggio in batch di un cliente: ' + e);
            return 0;
        }
            
    } else {
        try {
            await setDoc(doc(db, 'clienti', String(leggiInfoDB(tempCliente).id)),
                creaClientePerSalvataggio(tempCliente, Timestamp.now()), { merge: true }
            );
            return 1;
        } catch(e) {
            console.error('Errore durante il salvataggio di un cliente: ' + e);
            return 0;
        }
    }
}

export async function eliminaClienteInFirestore(idCliente) {
    // Elimina un cliente basandosi sulla proprietà id
    // Restituisce 1 se ok, 0 in caso di fallimento
    
    try {
        await deleteDoc(doc(db, 'clienti', String(idCliente)));
        delete dbClienti.value[idCliente];
        return 1;
    } catch(e) {
        console.error('Errore durante il salvataggio di un cliente: ' + e);
        return 0;
    }
}

export async function eliminaLeadInFirestore(idCliente, idLead) {
    // Elimina il lead
    // Restituisce 1 se ok, 0 in caso di fallimento
    const strField = 'leads.' + idLead;
    try {
        await updateDoc(doc(db, 'clienti', String(idCliente)),{
            [strField]: deleteField()
        });
        return 1;
    } catch(e) {
        console.error('Errore durante l\'eliminazione di un lead: ' + e);
        return 0;
    }
}

export async function eliminaLavoroInFirestore(idCliente, idLavoro) {
    // Elimina un lead basandosi sulla proprietà id del cliente e del lead
    const strField = 'lavori.' + idLavoro;
    try {
        await updateDoc(doc(db, 'clienti', String(idCliente)),{
            [strField]: deleteField()
        });
        return 1;
    } catch(e) {
        console.error('Errore durante l\'eliminazione di un lavoro: ' + e);
        return 0;
    }
}

export async function eliminaContattoDaLavoroInFirestore(idCliente, idLavoro, idContatto) {
    // Elimina un contatto basandosi sulla proprietà id del cliente e del lead
    const strField = 'lavori.' + idLavoro + '.contatti.' + idContatto;
    try {
        await updateDoc(doc(db, 'clienti', String(idCliente)),{
            [strField]: deleteField()
        });
        return 1;
    } catch(e) {
        console.error('Errore durante l\'eliminazione di un contatto da un lavoro: ' + e);
        return 0;
    }
}

export async function eliminaContattoDaLeadInFirestore(idCliente, idLead, idContatto) {
    // Elimina un contatto basandosi sulla proprietà id del cliente e del lead
    const strField = 'leads.' + idLead + '.contatti.' + idContatto;
    try {
        await updateDoc(doc(db, 'clienti', String(idCliente)),{
            [strField]: deleteField()
        });
        return 1;
    } catch(e) {
        console.error('Errore durante l\'eliminazione di un contatto da un lead: ' + e);
        return 0;
    }
}

export async function eliminaFaseInFirestore(idCliente, idLavoro, idFase) {
    // Elimina una fase
    const strField = 'lavori.' + idLavoro + '.fasi.' + idFase;
    try {
        await updateDoc(doc(db, 'clienti', String(idCliente)),{
            [strField]: deleteField()
        });
        return 1;
    } catch(e) {
        console.error('Errore durante l\'eliminazione di una fase: ' + e);
        return 0;
    }
}

export async function eliminaCostoInFirestore(idCliente, idLavoro, idCosto) {
    // Elimina una voce di costo
    const strField = 'lavori.' + idLavoro + '.costi.' + idCosto;
    try {
        await updateDoc(doc(db, 'clienti', String(idCliente)),{
            [strField]: deleteField()
        });
        return 1;
    } catch(e) {
        console.error('Errore durante l\'eliminazione di una voce di costo: ' + e);
        return 0;
    }
}

export async function eliminaContabileInFirestore(idCliente, idLavoro, idContabile) {
    // Elimina una contabile
    const strField = 'lavori.' + idLavoro + '.contabili.' + idContabile;
    try {
        await updateDoc(doc(db, 'clienti', String(idCliente)),{
            [strField]: deleteField()
        });
        return 1;
    } catch(e) {
        console.error('Errore durante l\'eliminazione di una contabile: ' + e);
        return 0;
    }
}

export async function eliminaDicoInFirestore(idCliente, idLavoro, idDico) {
    // Elimina una dico
    const strField = 'lavori.' + idLavoro + '.dico.' + idDico;
    try {
        await updateDoc(doc(db, 'clienti', String(idCliente)),{
            [strField]: deleteField()
        });
        return 1;
    } catch(e) {
        console.error('Errore durante l\'eliminazione di una dico: ' + e);
        return 0;
    }
}

export async function eliminaPreventivoInFirestore(idCliente, idLead, idPreventivo) {
    // Elimina un preventivo
    const strField = 'leads.' + idLead + '.preventivi.' + idPreventivo;
    try {
        await updateDoc(doc(db, 'clienti', String(idCliente)),{
            [strField]: deleteField()
        });
        return 1;
    } catch(e) {
        console.error('Errore durante l\'eliminazione di un preventivo: ' + e);
        return 0;
    }
}

export async function eliminaScontoDaPreventivoInFirestore(idCliente, idLead, idPreventivo, idSconto) {
    // Elimina uno sconto da un preventivo
    const strField = 'leads.' + idLead + '.preventivi.' + idPreventivo + '.sconti.' + idSconto;
    try {
        await updateDoc(doc(db, 'clienti', String(idCliente)),{
            [strField]: deleteField()
        });
        return 1;
    } catch(e) {
        console.error('Errore durante l\'eliminazione di un preventivo: ' + e);
        return 0;
    }
}

export async function cambiaClienteALeadInFirestore(idCliente, idLead, idNuovoCliente) {
    // Copia il lead nel nuovo cliente

    // Creo un cliente temporaneo per il salvataggio
    let tempCliente = {}
    aggiungiInfoDB(tempCliente, {
        id: idNuovoCliente
    });
    tempCliente.leads = {};
    tempCliente.leads[idLead] = {};
    tempCliente.leads[idLead] = dbClienti.value[idCliente].leads[idLead];

    // Operazioni in batch
    const batch = writeBatch(db);
    // Aggiungi il lead al nuovo cliente
    batch.set(doc(db, 'clienti', String(leggiInfoDB(tempCliente).id)),
        creaClientePerSalvataggio(tempCliente, Timestamp.now()), { merge: true }
    );
    // Rimuovi il lead dal vecchio cliente
    const strField = 'leads.' + idLead;
    batch.update(doc(db, 'clienti', String(idCliente)),{
        [strField]: deleteField()
    });
    // Commit del batch
    await batch.commit();
}

export async function cambiaClienteALavoroInFirestore(idCliente, idLavoro, idNuovoCliente) {
    // Copia il lead nel nuovo cliente

    // 1. Recupera preventivamente tutte le aree riservate associate al lavoro (limbo e storiche attive)
    const qAree = query(
        collection(db, 'aree_riservate_clienti'),
        where('idLavoro', '==', Number(idLavoro))
    );
    const areeSnapshot = await getDocs(qAree);

    // Creo un cliente temporaneo per il salvataggio
    let tempCliente = {}
    aggiungiInfoDB(tempCliente, {
        id: idNuovoCliente
    });
    tempCliente.lavori = {};
    tempCliente.lavori[idLavoro] = {};
    tempCliente.lavori[idLavoro] = dbClienti.value[idCliente].lavori[idLavoro];

    // Operazioni in batch
    const batch = writeBatch(db);
    // Aggiungi il lavoro al nuovo cliente
    batch.set(doc(db, 'clienti', String(leggiInfoDB(tempCliente).id)),
        creaClientePerSalvataggio(tempCliente, Timestamp.now()), { merge: true }
    );
    // Rimuovi il lavoro dal vecchio cliente
    const strField = 'lavori.' + idLavoro;
    batch.update(doc(db, 'clienti', String(idCliente)),{
        [strField]: deleteField(),
        modified: Timestamp.now()
    });

    // 2. Includi nel batch l'aggiornamento dell'idCliente per tutti i token associati
    areeSnapshot.forEach((docArea) => {
        const areaRef = doc(db, 'aree_riservate_clienti', docArea.id);
        batch.update(areaRef, {
            idCliente: Number(idNuovoCliente)
        });
        console.log(`[BATCH] Token ${docArea.id} incluso nello spostamento verso il cliente ${idNuovoCliente}`);
    });

    // 3. Esegui il commit atomico di tutto
    await batch.commit();
}

/**
 * Pulisce il payload prima del salvataggio su Firestore.
 * Rimuove ricorsivamente la proprietà '_info' generata dalla Cloud Function
 * per i dati freddi, impedendo che venga scritta nel database.
 */
function pulisciPayloadDaInfo(obj) {
    // Se non è un oggetto o è null, ritorna il valore così com'è
    if (obj === null || typeof obj !== 'object') return obj;
    // Non tocchiamo i Timestamp nativi di Firebase
    if (obj instanceof Timestamp || obj instanceof Date) return obj;
    
    // Se è un array, puliamo ogni elemento interno
    if (Array.isArray(obj)) {
        return obj.map(pulisciPayloadDaInfo);
    }

    const clone = {};
    for (const key in obj) {
        if (key === '_info') continue; // IL CUORE DELLA FIX: Ignora i metadati freddi
        clone[key] = pulisciPayloadDaInfo(obj[key]);
    }
    return clone;
}

/**
 * Aggiunge o aggiorna le informazioni associate a un cliente.
 * Crea automaticamente l'oggetto se non esiste già.
 */
export function aggiungiInfoDB(elemento, dati = {}) {
  // 1. SCUDO GLOBALE: Se l'elemento è null o undefined, usciamo subito.
  if (!elemento) return null;

  const key = toRaw(isRef(elemento) ? elemento.value : elemento); // chiave stabile (no proxy)
  
  // 2. SCUDO GLOBALE: La WeakMap accetta SOLO oggetti.
  if (typeof key !== 'object') return null;

  let info = infoDB.get(key);

  // Se non esiste, crea un oggetto reattivo
  if (!info) {
    info = reactive({});
    infoDB.set(key, info);
  }

  // Merge profondo (senza gestione speciale per array)
  deepMerge(info, dati);
  return info;
}

function _keyOf(el) {
  const k = isRef(el) ? el.value : el;
  if (!k || typeof k !== 'object') return null;   // niente chiavi primitive
  return toRaw(k);                                 // chiave stabile (no proxy)
}

export function leggiInfoDB(elemento, fallback = null) {
  const key = _keyOf(elemento);
  if (!key) return fallback;

  // 1. Cerca nella WeakMap (Dati REATTIVI da Firestore)
  const infoReattive = infoDB.get(key);
  if (infoReattive && Object.keys(infoReattive).length > 0) {
    return infoReattive;
  }

  // 2. Fallback sui dati PRE-CALCOLATI (Dati FREDDI da Storage)
  if (key._info) {
    return key._info;
  }

  return fallback;
}

export function leggiInfoDBRaw(elemento) {
  return toRaw(leggiInfoDB(elemento));
}

/**
 * Merge profondo semplice: copia le proprietà annidate
 * senza sostituire gli oggetti intermedi.
 */
function deepMerge(target, source) {
  for (const key of Object.keys(source)) {
    const sv = source[key];
    const tv = target[key];

    if (isPlainObject(sv) && isPlainObject(tv)) {
      deepMerge(tv, sv);
    } else {
      target[key] = sv;
    }
  }
}

function isPlainObject(v) {
  return v && typeof v === 'object' && !Array.isArray(v);
}

export async function aggiungiTagLeadInFirestore(nuovoTag) {
    // Aggiunge un nuovo tag al vocabolario globale in utilita/impostazioni
    if (!nuovoTag || typeof nuovoTag !== 'string') return 0;
    
    const tagFormattato = nuovoTag.trim();
    if (tagFormattato === '') return 0;

    try {
        const docRef = doc(db, 'utilita', 'impostazioni');
        // arrayUnion aggiunge l'elemento solo se non è già presente nell'array su Firestore
        await updateDoc(docRef, {
            tagsLeads: arrayUnion(tagFormattato)
        });
        return 1;
    } catch(e) {
        console.error('Errore durante l\'aggiunta del tag al vocabolario globale: ' + e);
        return 0;
    }
}

export async function testStop() {
    // Funzione di test
    let tempTest = {mostraTest: false};
    try {
        await setDoc(doc(db, 'utilita', 'impostazioni'),
            tempTest, { merge: true }
        );
        return 1;
    } catch(e) {
        console.error('Errore durante il salvataggio del test: ' + e);
        return 0;
    }
}